/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Login process saga for common and service access users
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.11.0
 */

import find from 'lodash/find';
import get from 'lodash/get';
import { SagaIterator } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';

import { setNotificationsSettings } from '@/main/modules/userNotifications/slice';
import { requestSaga } from '@/main/sagas/requestSaga';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { API_HOSTNAME, LOGIN, LOGIN_SERVICE_ACCESS, USER_TIMEZONE } from '@modules/app/Api';
import { HttpStatus } from '@modules/app/appConstants';
import { appHistoryPush } from '@modules/app/appHistory';
import { makeSelectAppErrorCode, makeSelectOperatorSettings } from '@modules/app/appSelectors';
import { hideCaptcha, showCaptcha } from '@modules/captcha/captchaActions';
import { makeSelectTranslation } from '@modules/internationalization/intSelectors';
import { ITranslation } from '@modules/internationalization/types';
import { hideLoader, setTextLoader, showLoader } from '@modules/loader/loaderActions';
import { logBusinessEvent } from '@modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { openNotification } from '@modules/notification/notificationActions';
import { ENotificationLevelTypes, ENotificationTypes } from '@modules/notification/types/notificationTypes';
import { setLocalStorageDataAction } from '@modules/storage/storageActions';
import { ELocalStorageKeys } from '@modules/storage/storageTypes';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { RoutePath } from '@routes/constants/routesConstants';

import { loadAuthData, loginProcessFailed, loginProcessSuccessful } from '../actions/authActions';
import { cacheUserDataStart } from '../actions/authCacheActions';
import {
    loginProcessNewsServiceAccessErrorAction,
    loginProcessNewsServiceAccessSuccessfulAction,
    loginProcessServiceAccessErrorAction,
    loginProcessServiceAccessSuccessfulAction,
} from '../actions/authServiceAccessActions';
import { getTemporaryPassword } from '../actions/changePasswordActions';
import { CACHE_USER_DATA_START_SUCCESSFUL } from '../constants/authCacheConstants';
import {
    AuthorityRoleEnum,
    EServiceUserLoginResponseData,
    LOAD_AUTH_DATA_FAILURE,
    LOAD_AUTH_DATA_SUCCESSFUL,
} from '../constants/authConstants';
import { IAuthData, ILoginProcess } from '../types/authTypes';
import { authHandleForbiddenErrorSaga } from './authSaga';

const options = {
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-NODE': 'sticky',
    },
};

/**
 * 1. send login process response
 * 2. check on user exist (if exist)
 *      2.1. Check on authority
 *      2.2. Check on user cached status
 *      2.3. Set cookie
 *      2.4. Send user timezone
 *      2.5. Login success
 * 3. If user not exist - show notice
 * 4. Catch server error - show notice
 */
export function* loginProcessSaga(action: ILoginProcess): SagaIterator {
    const translation: ITranslation = yield select(makeSelectTranslation());
    yield put(hideCaptcha());
    try {
        yield put(showLoader());
        yield put(
            logBusinessEvent({
                eventName: ELogBusinessEventName.USER_AUTH,
                login: action.payload.body.username,
                type: ELogBusinessEventType.login,
            }),
        );

        // step 1 - submit login form data
        const response: IAuthData = yield call(
            requestSaga,
            API_HOSTNAME + LOGIN,
            'POST',
            objectToFormData(action.payload.body),
            options,
        );

        if (response.user) {
            // check Required change password
            if (find(response.authorities, { authority: AuthorityRoleEnum.ROLE_PASSWORD_CHANGE_REQUIRED })) {
                yield call(appHistoryPush, RoutePath.changePassword);
                yield put(getTemporaryPassword());
                return;
            }
            // step 2 - If cached is false we request a cache
            yield call(loginProcessStartUserCacheSaga, response, translation);
            // step 3 - Set default accountId to cookie
            yield call(loginProcessSetCookieSaga, response);
            // step 4 - Send browser timezone
            yield call(loginProcessSendBrowserTimezoneSaga, translation);
            // step 5 - Login success
            yield call(loginProcessSuccessSaga, response);
            // GTM: Send login success operation
            yield put(
                pushGTMAnalytic({
                    operation: GTMOperations.auth.onLoginSuccessOperation,
                    withSusrId: true,
                    withUserRole: true,
                }),
            );
            yield put(
                wizardStorageClearStepsAction({
                    wizardName: EWizardName.IMPORT_PERSONAL_DATA,
                }),
            );
        } else {
            const errorCode = yield call(loginProcessNoUserFailSaga, response, translation);
            // GTM: Send login error operation
            GTMOperations.auth.onLoginErrorOperation(
                response.message ??
                    translation.common.error.codes[errorCode] ??
                    `${translation.common.error.defaultMessage} (${errorCode})`,
            );
        }

        // check on captcha status
        yield call(loginProcessCheckCaptchaSaga, response);

        if (response.notifications) {
            yield put(
                setNotificationsSettings({
                    showNotifications: response.notifications.showNotifications,
                    showNews: response.notifications.showNews,
                }),
            );
        }
    } catch (error) {
        const errorMessage = yield call(loginProcessFail, error, translation);
        if (errorMessage) {
            // GTM: Send login error operation
            GTMOperations.auth.onLoginErrorOperation(errorMessage);
        }
    } finally {
        yield put(hideLoader());
    }
}

export function* loginProcessServiceAccessSaga(action: ILoginProcess): SagaIterator {
    const translation = yield select(makeSelectTranslation());
    yield put(hideCaptcha());
    try {
        yield put(showLoader());
        const URL = API_HOSTNAME + LOGIN_SERVICE_ACCESS;
        const response = yield call(requestSaga, URL, 'POST', objectToFormData(action.payload.body), {
            ...options,
            throwIfHasError: true,
        });

        if (response.captchaShouldBeShown) {
            yield put(showCaptcha());
            return;
        }
        yield put(hideCaptcha());

        if (response.user) {
            // step 1 - If cached is false we request a cache
            yield call(loginProcessStartUserCacheSaga, response, translation);
            // step 2 - Set default accountId to cookie
            yield call(loginProcessSetCookieSaga, response);
            // step 3 - Send browser timezone
            yield call(loginProcessSendBrowserTimezoneSaga, translation);
            // step 4 - Login success
            yield call(loginProcessSuccessSaga, response);
        } else {
            if (response === EServiceUserLoginResponseData.NEWS_SERVICE_USER) {
                yield put(loginProcessNewsServiceAccessSuccessfulAction());
                yield put(loadAuthData());
            } else {
                yield put(loginProcessServiceAccessSuccessfulAction());
            }
        }
    } catch (response: any) {
        if (response?.data?.captchaShouldBeShown) {
            yield put(showCaptcha());
        } else {
            yield put(hideCaptcha());
        }
        yield put(loginProcessServiceAccessErrorAction());
        yield put(loginProcessNewsServiceAccessErrorAction());
        yield call(loginProcessFail, response.error || response, translation);
    } finally {
        yield put(hideLoader());
    }
}

export const objectToFormData = (obj: Record<string, any>) => {
    return Object.keys(obj)
        .map((key: string) => {
            return encodeURIComponent(key) + '=' + encodeURIComponent(get(obj, key));
        })
        .join('&');
};

/**
 * Если пользователь еще не закеширован, то отправляем запрос status на бек
 * Когда запрос status возращает 200, то выполняется CACHE_USER_DATA_START_SUCCESSFUL
 */
export function* loginProcessStartUserCacheSaga(response: IAuthData, translation: ITranslation): SagaIterator {
    if (!response.cached) {
        yield put(setTextLoader(translation.common.dataSync));
        yield put(cacheUserDataStart());
        yield take(CACHE_USER_DATA_START_SUCCESSFUL);
    }
}

/**
 * Set default accountId to cookie
 */
export function* loginProcessSetCookieSaga(response: IAuthData): SagaIterator {
    yield put(
        setLocalStorageDataAction({
            key: ELocalStorageKeys.DASHBOARD_SELECTED_ACCOUNT,
            value: {
                id: response.user.accountId.toString(),
            },
        }),
    );
}

/**
 * Отправка timezone пользователя на сервер
 */
export function* loginProcessSendBrowserTimezoneSaga(translation: ITranslation): SagaIterator {
    const zoneOffset = getBrowserTimeZone();
    try {
        yield call(requestSaga, API_HOSTNAME + USER_TIMEZONE, 'POST', { zoneOffset });
    } catch (e) {
        yield put(
            openNotification({
                message: translation.common.notification.errorServer,
                type: ENotificationTypes.AUTH,
                level: ENotificationLevelTypes.WARN,
            }),
        );
    }
}

export function getBrowserTimeZone() {
    const date = new Date();
    const utc = date.getTimezoneOffset() / -60;
    if (utc < 2 || utc > 12) {
        return 3;
    } else {
        return utc;
    }
}

/**
 * Успешная авторизация пользователя
 */
export function* loginProcessSuccessSaga(response: IAuthData): SagaIterator {
    if (response.cached) {
        yield put(loginProcessSuccessful(response));
    } else {
        yield put(loadAuthData());
        yield take([LOAD_AUTH_DATA_SUCCESSFUL, LOAD_AUTH_DATA_FAILURE]);
    }
}

/**
 * Ошибка при выполнении логина: Такой пользователь не существует
 */
export function* loginProcessNoUserFailSaga(response: IAuthData, translation: ITranslation): SagaIterator<string> {
    const errorCode: string = yield select(makeSelectAppErrorCode());
    let url: string | undefined;
    let type = ENotificationTypes.AUTH;
    let urlLabel = translation.common.notification.goToLabel;

    if (errorCode === 'user.is.b2c') {
        const operatorSettings = yield select(makeSelectOperatorSettings());
        url = operatorSettings.urls.personal;
    } else if (errorCode === 'user.service-access.denied') {
        type = ENotificationTypes.AUTH_SERVICE_ACCESS;
        url = RoutePath.serviceAccessLogin;
        urlLabel = translation.common.serviceUser.notificationGoToLabel;
    }
    yield put(
        openNotification({
            message: response.message,
            type,
            level: ENotificationLevelTypes.ERROR,
            urls: [{ url: url ?? '', label: url ? urlLabel : '' }],
        }),
    );

    return errorCode;
}

/**
 * Проверка на капчу
 */
export function* loginProcessCheckCaptchaSaga(response: IAuthData) {
    if (response.captchaShouldBeShown) {
        yield put(showCaptcha());
        // GTM: Send captcha show operation
        GTMOperations.auth.onCaptchaShowOperation();
    } else {
        yield put(hideCaptcha());
    }
}

/**
 * Ошибка при выполнении логина
 */
export function* loginProcessFail(error: any, translation: ITranslation): SagaIterator<string | undefined> {
    yield put(loginProcessFailed(error));
    if (error.code === HttpStatus.FORBIDDEN) {
        yield call(authHandleForbiddenErrorSaga);
        return;
    }
    const errorMessage = translation.common.error.codes[error.code] || translation.common.notification.errorServer;
    yield put(
        openNotification({
            message: errorMessage,
            type: ENotificationTypes.AUTH,
            level: ENotificationLevelTypes.ERROR,
        }),
    );

    return errorMessage;
}
